import React from 'react';
import { DatePicker } from '@material-ui/pickers';
import { IconButton, makeStyles, Typography } from '@material-ui/core';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { ParsableDate } from '@material-ui/pickers/constants/prop-types';
// import { FixMeLater } from 'src/types';
import ClearIcon from '@material-ui/icons/Clear';
import { dateTodayVanilla } from 'src/utils';
import clsx from 'clsx';
import { theme } from 'src/theme';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';

interface Props {
  title?: string;
  dateFromLabel?: string;
  dateToLabel?: string;
  fromDateMin?: string | null;
  fromDateMax?: string | null;
  fromDateValue?: ParsableDate;
  toDateMin?: string | null;
  toDateMax?: string | null;
  toDateValue?: ParsableDate;
  hasNoDateToday?: boolean;
  fullwidth?: boolean;
  onChangeFromDate: (date: MaterialUiPickersDate) => void;
  onChangeToDate: (date: MaterialUiPickersDate) => void;
}

const useStyles = makeStyles((theme) => ({
  root: {},
  datePickersContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  dashSeparator: {
    marginRight: theme.spacing(1),
    marginLeft: theme.spacing(1)
  },
  highlightedDay: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    borderRadius: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '4px',
    boxSizing: 'border-box'
  },
  startDay: {
    color: theme.palette.primary.dark,
    borderRadius: '4px 0 0 4px',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '4px',
    boxSizing: 'border-box'
  },
  endDay: {
    color: theme.palette.primary.dark,
    borderRadius: '0 4px 4px 0',
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '4px',
    boxSizing: 'border-box'
  },
  dayWrapper: {
    position: 'relative',
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    boxSizing: 'border-box'
  },
  dayContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 1
  },
  rangeHighlight: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    backgroundColor: '#239EF7',
    opacity: 0.2,
    zIndex: 0
  },
  rangeHighlightStart: {
    left: '0%',
    right: 0,
    borderTopLeftRadius: '4px',
    borderBottomLeftRadius: '4px',
    opacity: 0.2
  },
  rangeHighlightMiddle: {
    left: 0,
    right: 0
  },
  rangeHighlightEnd: {
    left: 0,
    right: '0%',
    borderTopRightRadius: '4px',
    borderBottomRightRadius: '4px',
    opacity: 0.2
  },
  singleDayHighlight: {
    borderRadius: '4px',
    left: '50%',
    right: '50%',
    marginLeft: '-18px',
    marginRight: '-18px'
  },
  disabledDay: {
    color: theme.palette.text.disabled,
    pointerEvents: 'none',
    opacity: 0.5
  }
}));

export const DatePickerRangeComponent = ({
  title,
  dateToLabel = 'To',
  dateFromLabel = 'From',
  fromDateMax = undefined,
  fromDateMin = undefined,
  toDateMin = undefined,
  fromDateValue,
  toDateValue,
  toDateMax,
  hasNoDateToday = false,
  fullwidth = false,
  onChangeFromDate,
  onChangeToDate
}: Props) => {
  const classes = useStyles();

  // Helper to check if a day is between fromDateValue and toDateValue
  const isBetween = (day: Date, start?: Date | null, end?: Date | null) => {
    if (!start || !end) return false;
    return day > start && day < end;
  };

  // Customize day rendering to highlight range
  const renderDay = (
    day: Date | null,
    _selectedDate: Date | null,
    isInCurrentMonth: boolean,
    dayComponent: React.ReactElement
  ) => {
    if (!isInCurrentMonth || !day)
      return (
        <div className={classes.dayWrapper}>
          <div className={classes.dayContainer}>{dayComponent}</div>
        </div>
      );

    const start =
      fromDateValue &&
      (typeof fromDateValue === 'string' ||
        typeof fromDateValue === 'number' ||
        fromDateValue instanceof Date)
        ? new Date(fromDateValue)
        : null;
    const end =
      toDateValue &&
      (typeof toDateValue === 'string' ||
        typeof toDateValue === 'number' ||
        toDateValue instanceof Date)
        ? new Date(toDateValue)
        : null;

    const isSameDay = (d1: Date, d2: Date) =>
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to midnight

    const isAfterToday = day > today;
    const isDisabledDay = isAfterToday && !hasNoDateToday;

    const isStart = start && isSameDay(day, start);
    const isEnd = end && isSameDay(day, end);

    const isInRange = isBetween(day, start, end);
    const isSingleDay = isStart && isEnd;

    let dayHighlightClass = '';
    if (isStart && !isEnd) dayHighlightClass = classes.startDay;
    else if (isEnd && !isStart) dayHighlightClass = classes.endDay;
    else if (isSingleDay) dayHighlightClass = classes.highlightedDay;

    return (
      <div className={classes.dayWrapper}>
        {/* Range highlight background */}
        {isInRange && !isStart && !isEnd && (
          <div
            className={clsx(
              classes.rangeHighlight,
              classes.rangeHighlightMiddle
            )}
          />
        )}
        {isStart && !isEnd && (
          <div
            className={clsx(
              classes.rangeHighlight,
              classes.rangeHighlightStart
            )}
          />
        )}
        {isEnd && !isStart && (
          <div
            className={clsx(classes.rangeHighlight, classes.rangeHighlightEnd)}
          />
        )}
        {isSingleDay && (
          <div
            className={clsx(classes.rangeHighlight, classes.singleDayHighlight)}
          />
        )}

        {/* Day content */}
        <div className={classes.dayContainer}>
          {React.cloneElement(dayComponent, {
            className: clsx(
              dayHighlightClass,
              isDisabledDay && classes.disabledDay
            ),
            style: {
              ...dayComponent.props.style,
              color:
                isStart || isEnd || isSingleDay || isInRange
                  ? theme.palette.primary.dark
                  : isDisabledDay
                  ? theme.palette.text.disabled
                  : undefined,
              fontWeight: isStart || isEnd || isSingleDay ? 'bold' : undefined
            }
          })}
        </div>
      </div>
    );
  };

  const onClearPress = (
    e: React.MouseEvent<HTMLButtonElement>,
    item: 'to' | 'from'
  ) => {
    e.stopPropagation();
    if (item === 'from') {
      onChangeFromDate(null);
    }
    if (item === 'to') {
      onChangeToDate(null);
    }
  };

  return (
    <div style={{ width: fullwidth ? '100%' : 'auto' }}>
      {title ? (
        <Typography align="center" variant="h5" style={{ marginBottom: 8 }}>
          {title}
        </Typography>
      ) : null}
      <div className={classes.datePickersContainer}>
        <DatePicker
          autoOk
          fullWidth={fullwidth}
          format="MMMM dd, yyyy"
          inputVariant="outlined"
          name="from"
          animateYearScrolling
          value={fromDateValue}
          variant="inline"
          minDate={fromDateMin}
          label={dateFromLabel}
          maxDate={fromDateMax}
          onChange={onChangeFromDate}
          renderDay={renderDay}
          InputProps={{
            endAdornment: (
              <IconButton size="small" onClick={(e) => onClearPress(e, 'from')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            )
          }}
        />
        <ArrowForwardIcon style={{ marginLeft: 3, marginRight: 3 }} />
        {/* <Typography
          align="center"
          variant="h1"
          className={classes.dashSeparator}
        >
          -
        </Typography> */}
        <DatePicker
          autoOk
          fullWidth={fullwidth}
          format="MMMM dd, yyyy"
          inputVariant="outlined"
          name="to"
          animateYearScrolling
          value={toDateValue}
          variant="inline"
          minDate={toDateMin}
          maxDate={
            toDateMax || (hasNoDateToday ? undefined : dateTodayVanilla())
          }
          label={dateToLabel}
          onChange={onChangeToDate}
          renderDay={renderDay}
          InputProps={{
            endAdornment: (
              <IconButton size="small" onClick={(e) => onClearPress(e, 'to')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            )
          }}
          disabled={!fromDateValue}
        />
      </div>
    </div>
  );
};
